import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { InputNumber } from "primereact/inputnumber";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { useChartsStore } from "../../../store/chartStore";
import { effectiveAutoBoxSize } from "@chartLib/lib/calculator/pointAndFigure";
import ConfigPill, {
  PILL_HINT_STYLE,
  FLYOUT_SECTION_STYLE,
  FLYOUT_LABEL_STYLE,
  RADIO_ROW_STYLE,
  RADIO_ROW_LAST_STYLE,
  RADIO_LABEL_STYLE,
  FLYOUT_INPUT_STYLE,
} from "./ConfigPill";

/**
 * PointAndFigureBoxIndicator - Shows the P&F box size × reversal on the chart
 *
 * Displays the current settings (e.g., "Auto 50×3") and opens a flyout
 * when clicked to change them. Chrome shared with RenkoBrickSizeIndicator
 * via ConfigPill. Auto mode uses the ChartIQ traditional scale table
 * (chartIQBoxSize).
 */
const PointAndFigureBoxIndicator = ({ chartId, chartData, inline = false }) => {
  const chart = useChartsStore((state) =>
    state.charts.find((c) => c.id === chartId),
  );
  const updateChartConfig = useChartsStore((state) => state.updateChartConfig);
  // Barras CRUAS — é o array que o calculator recebe, e a chave do box efetivo
  // do modo auto (#432). O prop chartData vem já agregado (colunas P&F) em um dos
  // call sites, então não serve para essa consulta.
  const rawData = useChartsStore((state) => state.getData(chartId));

  // Config atual (mesmo shape do PointAndFigureSettingsModal)
  const currentConfig = chart?.pointFigureConfig || {
    boxSize: null,
    reversal: 3,
  };
  const isAuto = currentConfig.boxSize === null;

  // Estado local de edição
  const [mode, setMode] = useState(isAuto ? "auto" : "fixed");
  const [boxSize, setBoxSize] = useState(currentConfig.boxSize || 1);
  const [reversal, setReversal] = useState(currentConfig.reversal || 3);

  // Box efetivo para exibição. No modo auto é o da tabela tradicional, EXCETO
  // quando o teto de marks obrigou o calculator a escalá-lo (#432) — aí o número
  // exibido tem que ser o que está desenhado, não o da tabela.
  const effectiveBoxSize = useMemo(() => {
    const config = chart?.pointFigureConfig || { boxSize: null };
    if (config.boxSize > 0) return config.boxSize;
    const source = rawData?.length ? rawData : chartData;
    if (!source || source.length === 0) return 1;
    return effectiveAutoBoxSize(source);
  }, [chart?.pointFigureConfig, chartData, rawData]);

  const effectiveReversal = currentConfig.reversal || 3;

  const formatBoxSize = (size) => {
    if (size >= 100) {
      return Math.round(size).toString();
    } else if (size >= 1) {
      const rounded = Math.round(size * 100) / 100;
      return rounded % 1 === 0
        ? rounded.toString()
        : rounded.toFixed(2).replace(/\.?0+$/, "");
    }
    return size.toFixed(4).replace(/\.?0+$/, "");
  };

  // Reabre com o config vigente
  const handleOpen = () => {
    const config = chart?.pointFigureConfig || { boxSize: null, reversal: 3 };
    setMode(config.boxSize === null ? "auto" : "fixed");
    setBoxSize(config.boxSize || Math.round(effectiveBoxSize) || 1);
    setReversal(config.reversal || 3);
  };

  const makeApply = (close) => () => {
    const pointFigureConfig = {
      boxSize: mode === "fixed" ? boxSize : null,
      reversal: reversal || 3,
    };
    updateChartConfig(chartId, { pointFigureConfig });
    close();
  };

  return (
    <ConfigPill
      inline={inline}
      title="Clique para configurar o Ponto & Figura"
      triggerEvoId="pandf-indicator-btn-open-settings"
      flyoutTitle={<>Configurar Ponto &amp; Figura</>}
      onOpen={handleOpen}
      trigger={
        <>
          <span style={PILL_HINT_STYLE}>{isAuto ? "Auto" : ""}</span>
          <span>
            {formatBoxSize(effectiveBoxSize)}×{effectiveReversal}
          </span>
        </>
      }
    >
      {({ close }) => (
        <>
          {/* Modo do box */}
          <div style={FLYOUT_SECTION_STYLE}>
            <div style={RADIO_ROW_STYLE}>
              <RadioButton
                inputId={`pfModeAuto-${chartId}`}
                name={`pfMode-${chartId}`}
                value="auto"
                checked={mode === "auto"}
                onChange={(e) => setMode(e.value)}
              />
              <label
                htmlFor={`pfModeAuto-${chartId}`}
                style={RADIO_LABEL_STYLE}
              >
                Automatico (escala tradicional)
              </label>
            </div>

            <div style={RADIO_ROW_LAST_STYLE}>
              <RadioButton
                inputId={`pfModeFixed-${chartId}`}
                name={`pfMode-${chartId}`}
                value="fixed"
                checked={mode === "fixed"}
                onChange={(e) => setMode(e.value)}
              />
              <label
                htmlFor={`pfModeFixed-${chartId}`}
                style={RADIO_LABEL_STYLE}
              >
                Tamanho Fixo
              </label>
            </div>
          </div>

          {mode === "fixed" && (
            <div style={FLYOUT_SECTION_STYLE}>
              <label style={FLYOUT_LABEL_STYLE}>Tamanho do Box (pontos)</label>
              <InputNumber
                value={boxSize}
                onValueChange={(e) => setBoxSize(e.value)}
                min={0.0001}
                step={0.0001}
                minFractionDigits={0}
                maxFractionDigits={4}
                style={FLYOUT_INPUT_STYLE}
                inputStyle={FLYOUT_INPUT_STYLE}
                data-evo-id="pandf-indicator-input-fixed-size"
              />
            </div>
          )}

          {/* Reversão (boxes) */}
          <div style={FLYOUT_SECTION_STYLE}>
            <label style={FLYOUT_LABEL_STYLE}>Reversao (boxes)</label>
            <InputNumber
              value={reversal}
              onValueChange={(e) => setReversal(e.value)}
              min={1}
              max={10}
              step={1}
              showButtons
              style={FLYOUT_INPUT_STYLE}
              inputStyle={FLYOUT_INPUT_STYLE}
              data-evo-id="pandf-indicator-input-reversal"
            />
          </div>

          <Button
            label="Aplicar"
            onClick={makeApply(close)}
            size="small"
            style={FLYOUT_INPUT_STYLE}
            data-evo-id="pandf-indicator-btn-apply"
          />
        </>
      )}
    </ConfigPill>
  );
};

PointAndFigureBoxIndicator.propTypes = {
  chartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  chartData: PropTypes.array,
  inline: PropTypes.bool,
};

export default PointAndFigureBoxIndicator;
